import React, { useState, useEffect } from 'react';
import { Instagram, Play, Loader2, AlertCircle, ArrowRight } from 'lucide-react';

const ReviewsSection = () => {
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const INSTAGRAM_USER_ID = "17841462114272706";
  const ACCESS_TOKEN ="EAApZAXWFzsXgBQO3JCw9ogoMpDuRQqfOLZB2ZAjuc1ocKZBXZB4TEsZAa9Ay1uStu7H0e6OBq6g6jYWBFN0uaq9vZBOneEAas3ZCOvpS0wV9MJV7RDVFOr2r1t3CO3CX4jsbrENmB4clZBYwfHBY5wlDlBexJUzZBrT9MvDGCNGMwaevDX0rZBFicDZBuz8pDVmq5ZCZAVOAZDZD";

  useEffect(() => {
    const checkWidth = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkWidth();
    window.addEventListener('resize', checkWidth);
    
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  useEffect(() => {
    fetchInstagramPosts();
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || instagramPosts.length === 0) return;
    
    const slidesCount = isMobile ? instagramPosts.length : Math.ceil(instagramPosts.length / 3);
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slidesCount);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [isAutoPlaying, instagramPosts.length, isMobile]);

  const fetchInstagramPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      setDebugInfo(null);

      const userUrl = `https://graph.facebook.com/v19.0/${INSTAGRAM_USER_ID}?fields=id,username&access_token=${ACCESS_TOKEN}`;
      
      const userResponse = await fetch(userUrl);
      const userData = await userResponse.json();
      
      if (!userResponse.ok) {
        throw new Error(`User API Error: ${userData.error?.message || 'Unknown error'}`);
      }
      
      const mediaUrl = `https://graph.facebook.com/v19.0/${INSTAGRAM_USER_ID}/media?fields=id,media_type,media_url,thumbnail_url,caption,permalink,timestamp,like_count,comments_count&access_token=${ACCESS_TOKEN}&limit=12`;
      
      const response = await fetch(mediaUrl);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(`Media API Error: ${data.error?.message || 'Unknown error'}`);
      }

      if (!data.data || data.data.length === 0) {
        throw new Error('No posts found. Make sure you have posts on your Instagram account.');
      }

      const posts = data.data.map((item) => {
        const isReel = item.media_type === "VIDEO";
        
        let title = "Instagram Post";
        if (item.caption) {
          const cleanCaption = item.caption.replace(/\n/g, ' ');
          const words = cleanCaption.split(' ').slice(0, 6).join(' ');
          title = words + (cleanCaption.split(' ').length > 6 ? '...' : '');
        }

        return {
          id: item.id,
          url: item.permalink,
          type: isReel ? 'reel' : 'post',
          title: title || (isReel ? 'Instagram Reel' : 'Instagram Post'),
          mediaUrl: item.media_url,
          thumbnailUrl: item.thumbnail_url || item.media_url,
          timestamp: item.timestamp,
          likes: item.like_count || 0,
          comments: item.comments_count || 0,
          caption: item.caption ? item.caption.substring(0, 100) + (item.caption.length > 100 ? '...' : '') : null
        };
      });

      setInstagramPosts(posts);
      setDebugInfo({
        user: userData.username,
        postsCount: posts.length,
        lastUpdated: new Date().toLocaleTimeString()
      });
      setLoading(false);
      
    } catch (err) {
      console.error('Instagram API Error Details:', err);
      setError(err.message);
      setLoading(false);
      setInstagramPosts([]);
    }
  };

  const refreshPosts = () => {
    fetchInstagramPosts();
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentSlide(index);
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '60px 20px', 
        textAlign: 'center',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        margin: '20px 0'
      }}>
        <Loader2 className="animate-spin" size={48} color="#667eea" style={{ margin: '0 auto 20px' }} />
        <p style={{ color: '#64748b', fontSize: '16px' }}>
          Loading Instagram posts from @dietitian_karpagam...
        </p>
      </div>
    );
  }

  const totalSlides = isMobile ? instagramPosts.length : Math.ceil(instagramPosts.length / 3);

  return (
    <section style={{ 
      padding: isMobile ? '40px 20px' : '60px 20px',
      background: '#ffffff',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative background elements */}
      <div style={{
        position: 'absolute',
        top: '-100px',
        right: '-100px',
        width: '300px',
        height: '300px',
        background: 'rgba(102, 126, 234, 0.05)',
        borderRadius: '50%',
        filter: 'blur(60px)'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-150px',
        left: '-150px',
        width: '400px',
        height: '400px',
        background: 'rgba(118, 75, 162, 0.05)',
        borderRadius: '50%',
        filter: 'blur(80px)'
      }} />

      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        position: 'relative', 
        zIndex: 1,
        padding: isMobile ? '0' : '0 20px'
      }}>
        {/* Header */}
        <div style={{ 
          textAlign: 'center',
          marginBottom: isMobile ? '30px' : '50px'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            background: '#00ba76',
            padding: '8px 20px',
            borderRadius: '30px',
            marginBottom: '20px'
          }}>
            <Instagram size={20} color="#fff" />
            <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
              @dietitian_karpagam
            </span>
          </div>
          
          <h2 style={{ 
            fontSize: isMobile ? '28px' : '42px',
            fontWeight: '700', 
            color: '#1e293b',
            margin: '0 0 15px 0'
          }}>
            From Our Instagram
          </h2>
          
          <p style={{
            color: '#64748b',
            fontSize: isMobile ? '16px' : '18px',
            maxWidth: '600px',
            margin: '0 auto',
            padding: isMobile ? '0 10px' : '0'
          }}>
            Science-backed guidance and real-life nutrition content curated from our Instagram feed.
          </p>
        </div>

        {error && (
          <div style={{ 
            background: '#fff', 
            borderRadius: '12px',
            padding: isMobile ? '16px' : '24px',
            marginBottom: '40px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <AlertCircle color="#dc2626" />
              <div>
                <h3 style={{ color: '#dc2626', margin: '0 0 8px 0', fontSize: isMobile ? '16px' : '18px' }}>
                  Could not load Instagram posts
                </h3>
                <p style={{ color: '#57534e', marginBottom: '12px', fontSize: isMobile ? '14px' : '16px' }}>{error}</p>
              </div>
            </div>
          </div>
        )}

        {debugInfo && (
          <div style={{ 
            textAlign: 'center',
            marginBottom: '30px'
          }}>
            <InteractiveHoverButton onClick={refreshPosts}>
              Refresh Posts
            </InteractiveHoverButton>
          </div>
        )}

        {instagramPosts.length > 0 ? (
          <div style={{ position: 'relative' }}>
            {/* Carousel Container */}
            <div style={{
              overflow: 'hidden',
              borderRadius: isMobile ? '12px' : '20px',
              position: 'relative'
            }}>
              <div style={{
                display: 'flex',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: `translateX(-${currentSlide * 100}%)`
              }}>
                {isMobile ? (
                  // Mobile: One post per slide
                  instagramPosts.map((post) => (
                    <div
                      key={post.id}
                      style={{
                        minWidth: '100%',
                        padding: '0 10px',
                        boxSizing: 'border-box'
                      }}
                    >
                      <a
                        href={post.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'block',
                          textDecoration: 'none',
                          color: '#1e293b'
                        }}
                      >
                        {/* Full-screen video/image */}
                        <div style={{ 
                          position: 'relative',
                          width: '100%',
                          height: '500px',
                          backgroundColor: post.type === 'reel' ? '#000' : '#f8fafc',
                          overflow: 'hidden',
                          borderRadius: '12px'
                        }}>
                          {post.thumbnailUrl ? (
                            <img 
                              src={post.thumbnailUrl} 
                              alt={post.title}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: post.type === 'reel' ? 'white' : '#64748b',
                              fontSize: '48px'
                            }}>
                              {post.type === 'reel' ? '🎬' : '📷'}
                            </div>
                          )}
                          
                          {/* Gradient overlay */}
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '50%',
                            background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                            pointerEvents: 'none'
                          }} />
                          
                          {/* Play button for reels */}
                          {post.type === 'reel' && (
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              background: 'linear-gradient(135deg, #E4405F, #C13584)',
                              borderRadius: '50%',
                              width: '70px',
                              height: '70px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backdropFilter: 'blur(8px)',
                              boxShadow: '0 8px 20px rgba(228, 64, 95, 0.4)'
                            }}>
                              <Play size={28} color="white" fill="white" style={{ marginLeft: '4px' }} />
                            </div>
                          )}
                          
                          {/* Type badge */}
                          <div style={{
                            position: 'absolute',
                            top: '16px',
                            right: '16px',
                            background: post.type === 'reel' 
                              ? 'linear-gradient(135deg, #E4405F, #C13584)' 
                              : 'linear-gradient(135deg, #667eea, #764ba2)',
                            color: 'white',
                            padding: '6px 14px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: '700',
                            letterSpacing: '0.5px',
                            backdropFilter: 'blur(8px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                          }}>
                            {post.type === 'reel' ? '▶ REEL' : '● POST'}
                          </div>
                        </div>
                      </a>
                    </div>
                  ))
                ) : (
                  // Desktop: Three posts per slide
                  Array.from({ length: Math.ceil(instagramPosts.length / 3) }).map((_, slideIndex) => (
                    <div
                      key={slideIndex}
                      style={{
                        minWidth: '100%',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '24px',
                        padding: '10px'
                      }}
                    >
                      {instagramPosts.slice(slideIndex * 3, slideIndex * 3 + 3).map((post, postIndex) => (
                        <a
                          key={post.id}
                          href={post.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'block',
                            background: 'white',
                            borderRadius: '16px',
                            overflow: 'hidden',
                            textDecoration: 'none',
                            color: '#1e293b',
                            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            animation: `fadeInUp 0.6s ease-out ${postIndex * 0.1}s both`,
                            height: '500px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 20px 50px rgba(0, 0, 0, 0.25)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0) scale(1)';
                            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.15)';
                          }}
                        >
                          {/* Full-height Thumbnail/Preview */}
                          <div style={{ 
                            position: 'relative', 
                            height: '100%',
                            width: '100%',
                            backgroundColor: post.type === 'reel' ? '#000' : '#f8fafc',
                            overflow: 'hidden'
                          }}>
                            {post.thumbnailUrl ? (
                              <img 
                                src={post.thumbnailUrl} 
                                alt={post.title}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  transition: 'transform 0.6s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                              />
                            ) : (
                              <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: post.type === 'reel' ? 'white' : '#64748b',
                                fontSize: '48px'
                              }}>
                                {post.type === 'reel' ? '🎬' : '📷'}
                              </div>
                            )}
                            
                            {/* Gradient overlay */}
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '50%',
                              background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                              pointerEvents: 'none'
                            }} />
                            
                            {/* Play button for reels */}
                            {post.type === 'reel' && (
                              <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                background: 'linear-gradient(135deg, #E4405F, #C13584)',
                                borderRadius: '50%',
                                width: '70px',
                                height: '70px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backdropFilter: 'blur(8px)',
                                boxShadow: '0 8px 20px rgba(228, 64, 95, 0.4)',
                                transition: 'transform 0.3s'
                              }}>
                                <Play size={28} color="white" fill="white" style={{ marginLeft: '4px' }} />
                              </div>
                            )}
                            
                            {/* Type badge */}
                            <div style={{
                              position: 'absolute',
                              top: '16px',
                              right: '16px',
                              background: post.type === 'reel' 
                                ? 'linear-gradient(135deg, #E4405F, #C13584)' 
                                : 'linear-gradient(135deg, #667eea, #764ba2)',
                              color: 'white',
                              padding: '6px 14px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: '700',
                              letterSpacing: '0.5px',
                              backdropFilter: 'blur(8px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                            }}>
                              {post.type === 'reel' ? '▶ REEL' : '● POST'}
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Dots Navigation */}
            {totalSlides > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                marginTop: isMobile ? '30px' : '40px'
              }}>
                {Array.from({ length: totalSlides }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    style={{
                      width: currentSlide === index ? '40px' : '12px',
                      height: '12px',
                      borderRadius: '6px',
                      border: 'none',
                      background: currentSlide === index 
                        ? '#00ba76' 
                        : '#e2e8f0',
                      cursor: 'pointer',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: currentSlide === index 
                        ? '0 4px 12px rgba(0, 186, 118, 0.4)' 
                        : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (currentSlide !== index) {
                        e.target.style.background = '#cbd5e1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentSlide !== index) {
                        e.target.style.background = '#e2e8f0';
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        ) : !error && (
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            background: '#f8fafc',
            borderRadius: '20px',
            border: '2px dashed #e2e8f0'
          }}>
            <Instagram size={48} color="#94a3b8" style={{ marginBottom: '16px' }} />
            <p style={{ color: '#64748b', fontSize: '16px' }}>
              No Instagram posts found. Please check your account settings.
            </p>
          </div>
        )}

        {/* View All Link */}
        <div style={{ 
          marginTop: isMobile ? '40px' : '50px',
          textAlign: 'center'
        }}>
          <a
            href="https://www.instagram.com/dietitian_karpagam/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              textDecoration: 'none'
            }}
          >
            <InteractiveHoverButton>
              View all posts on Instagram
            </InteractiveHoverButton>
          </a>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

const InteractiveHoverButton = ({ children, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        width: 'auto',
        cursor: 'pointer',
        overflow: 'hidden',
        borderRadius: '9999px',
        border: '2px solid #00ba76',
        padding: '12px 28px',
        textAlign: 'center',
        fontWeight: '600',
        background: '#ffffff',
        color: '#00ba76',
        fontSize: '14px',
        transition: 'all 0.3s',
        boxShadow: '0 4px 12px rgba(0, 186, 118, 0.2)',
        minWidth: '200px'
      }}
    >
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        justifyContent: 'center',
        position: 'relative'
      }}>
        <div style={{
          height: '8px',
          width: '8px',
          borderRadius: '50%',
          background: '#00ba76',
          transition: 'all 0.3s',
          transform: isHovered ? 'scale(100)' : 'scale(1)',
          flexShrink: 0
        }}></div>
        <span style={{
          display: 'inline-block',
          transition: 'all 0.3s',
          transform: isHovered ? 'translateX(60px)' : 'translateX(0)',
          opacity: isHovered ? 0 : 1,
          whiteSpace: 'nowrap'
        }}>
          {children}
        </span>
      </div>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 10,
        display: 'flex',
        height: '100%',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'all 0.3s',
        transform: isHovered ? 'translateX(0)' : 'translateX(100%)',
        opacity: isHovered ? 1 : 0,
        color: '#ffffff',
        paddingLeft: '4px'
      }}>
        <span style={{ whiteSpace: 'nowrap' }}>{children}</span>
        <ArrowRight size={18} style={{ flexShrink: 0 }} />
      </div>
    </button>
  );
};

export default ReviewsSection; 
